import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { ArrowLeft, Coins, Wallet, Gift, ShieldCheck, Clock } from 'lucide-react';
import clsx from 'clsx';
import { billingApi, formatPrice, type SwipaPackage, type BillingOrder } from '@/api/billing';
import { relayApi } from '@/api/relay';
import { formatSwipaSubunits, resolveSubunits } from '@/lib/swipaAmount';

function BalanceChip() {
  const { data } = useQuery({
    queryKey: ['swipa-balance'],
    queryFn: () => relayApi.balance(),
    refetchInterval: 15000,
  });
  const swipa = data
    ? formatSwipaSubunits(resolveSubunits(data.availableSub, data.available))
    : '—';
  return (
    <div className="flex items-center gap-1.5 bg-surface-800 rounded-lg px-3 py-1.5">
      <Coins size={14} className="text-yellow-400" />
      <span className="text-yellow-400 font-bold text-sm">{swipa}</span>
    </div>
  );
}

function PackageCard({ pkg, onBuy, busy }: { pkg: SwipaPackage; onBuy: () => void; busy: boolean }) {
  const total = pkg.swipaAmount + pkg.bonusSwipa;
  return (
    <div className="bg-surface-900 border border-surface-800 rounded-xl p-4 flex flex-col hover:border-brand-600/50 transition-colors">
      <div className="flex items-center gap-2">
        <Coins size={18} className="text-yellow-400" />
        <span className="text-2xl font-extrabold text-surface-50">{total.toLocaleString('ru-RU')}</span>
        <span className="text-surface-400 text-sm font-medium">SWIPA</span>
      </div>
      <p className="text-surface-400 text-xs mt-1">{pkg.name}</p>
      {pkg.bonusSwipa > 0 && (
        <span className="mt-2 inline-flex items-center gap-1 text-[11px] text-green-400 bg-green-500/10 rounded-md px-2 py-0.5 w-fit">
          <Gift size={11} /> +{pkg.bonusSwipa.toLocaleString('ru-RU')} бонус
        </span>
      )}
      <div className="flex-1" />
      <button
        onClick={onBuy}
        disabled={busy}
        className="btn-primary mt-4 w-full py-2 text-sm font-semibold disabled:opacity-40"
      >
        {busy ? '...' : `Купить за ${formatPrice(pkg.priceKopecks, pkg.currency)}`}
      </button>
    </div>
  );
}

const STATUS_LABEL: Record<string, { text: string; cls: string }> = {
  PENDING: { text: 'Ожидает оплаты', cls: 'text-amber-400' },
  PAID: { text: 'Оплачен', cls: 'text-blue-400' },
  CREDITED: { text: 'Зачислен', cls: 'text-green-400' },
  FAILED: { text: 'Ошибка', cls: 'text-red-400' },
  CANCELED: { text: 'Отменён', cls: 'text-surface-500' },
  REFUNDED: { text: 'Возврат', cls: 'text-purple-400' },
};

function RecentOrders() {
  const { data } = useQuery({
    queryKey: ['billing-orders'],
    queryFn: () => billingApi.listOrders(),
  });
  const orders = data?.data ?? [];
  if (!orders.length) return null;
  return (
    <div className="mt-8">
      <h3 className="text-surface-300 text-sm font-semibold mb-2 flex items-center gap-1.5">
        <Clock size={14} /> История пополнений
      </h3>
      <div className="space-y-1.5">
        {orders.map((o: BillingOrder) => {
          const st = STATUS_LABEL[o.status] ?? { text: o.status, cls: 'text-surface-400' };
          return (
            <div key={o.id} className="flex items-center justify-between bg-surface-900 border border-surface-800 rounded-lg px-3 py-2 text-sm">
              <div className="min-w-0">
                <span className="text-surface-100 font-medium">{o.swipaAmount.toLocaleString('ru-RU')} SWIPA</span>
                <span className="text-surface-500 text-xs ml-2">{new Date(o.createdAt).toLocaleDateString('ru-RU')}</span>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <span className="text-surface-400 text-xs">{formatPrice(o.amountKopecks, o.currency)}</span>
                <span className={clsx('text-xs font-medium', st.cls)}>{st.text}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function BillingPage() {
  const navigate = useNavigate();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['billing-packages'],
    queryFn: () => billingApi.listPackages(),
  });

  const checkoutMut = useMutation({
    mutationFn: async (packageId: string) => {
      setBusyId(packageId);
      setError(null);
      return billingApi.checkout(packageId);
    },
    onSuccess: ({ redirectUrl }) => {
      // Уводим на hosted-страницу оплаты провайдера.
      window.location.href = redirectUrl;
    },
    onError: (err: unknown) => {
      setBusyId(null);
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        || (err as Error).message || 'Не удалось начать оплату';
      setError(msg);
    },
  });

  const packages = data?.packages ?? [];
  const noProviders = data && data.providers.length === 0;

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-surface-950">
      {/* Header */}
      <div className="h-14 md:h-12 bg-surface-900 md:bg-transparent border-b border-surface-800 px-2 md:px-4 flex items-center gap-2 md:gap-3 flex-shrink-0">
        <button onClick={() => navigate(-1)} className="p-2 text-surface-400 rounded-full active:bg-surface-800 md:btn-ghost md:p-1.5">
          <ArrowLeft size={20} className="md:hidden" />
          <ArrowLeft size={18} className="hidden md:block" />
        </button>
        <Wallet size={18} className="text-brand-400 hidden md:block" />
        <div className="flex-1 min-w-0 md:flex-none">
          <p className="font-bold text-surface-50 md:text-surface-200 md:font-semibold text-base md:text-sm leading-tight">
            Пополнить SWIPA
          </p>
        </div>
        <div className="ml-auto"><BalanceChip /></div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        <div className="max-w-3xl mx-auto">
          <p className="text-surface-400 text-sm mb-4">
            SWIPA — внутренняя валюта платформы для оплаты функций, контента и стикеров. Выберите пакет:
          </p>

          {error && (
            <div className="mb-4 bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          {noProviders && (
            <div className="mb-4 bg-amber-500/10 border border-amber-500/30 text-amber-300 text-sm rounded-lg px-3 py-2">
              Приём оплаты временно не настроен. Загляните позже.
            </div>
          )}

          {isLoading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full" />
            </div>
          ) : !packages.length ? (
            <div className="text-center py-16 text-surface-500 text-sm">Пакеты пока недоступны</div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {packages.map((pkg) => (
                <PackageCard
                  key={pkg.id}
                  pkg={pkg}
                  busy={busyId === pkg.id}
                  onBuy={() => checkoutMut.mutate(pkg.id)}
                />
              ))}
            </div>
          )}

          <RecentOrders />

          {/* Юр.дисклеймер */}
          <div className="mt-8 flex items-start gap-2 text-surface-500 text-xs leading-relaxed">
            <ShieldCheck size={14} className="flex-shrink-0 mt-0.5 text-surface-600" />
            <p>
              SWIPA — цифровой контент-кредит для использования внутри платформы. Не является
              криптовалютой, цифровым финансовым активом или средством платежа, не подлежит выводу
              и обмену на денежные средства. Оплата — разовая покупка цифрового товара; фискальный
              чек формирует платёжный оператор. Совершая платёж, вы принимаете{' '}
              <a href="/terms" className="text-brand-400 hover:underline">условия оферты</a>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
